import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function parseTimeToSeconds(time: string): number {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

export function safeParseTimeToSeconds(time?: string | null): number {
  if (!time) return 0
  const parts = time.split(':').map(Number)
  if (parts.some((p) => Number.isNaN(p))) return 0
  const [hours = 0, minutes = 0, seconds = 0] = parts
  return hours * 3600 + minutes * 60 + seconds
}

export function timeStringToSeconds(time: string): number {
  const [hours, minutes, seconds] = time.split(':');
  const [sec, ms] = seconds.split('.');

  return (
    parseInt(hours) * 3600 +
    parseInt(minutes) * 60 +
    parseInt(sec) +
    (ms ? parseFloat('0.' + ms) : 0)
  );
}

export function formatDuration(totalSeconds: number, showCentiseconds = true): string {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return showCentiseconds ? "00:00:00.00" : "00:00:00"
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)
  const centiseconds = Math.round((totalSeconds - Math.floor(totalSeconds)) * 100)

  const paddedHours = String(hours).padStart(2, "0")
  const paddedMinutes = String(minutes).padStart(2, "0")
  const paddedSeconds = String(seconds).padStart(2, "0")

  if (!showCentiseconds) {
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
  }

  const paddedCentiseconds = String(centiseconds).padStart(2, "0")
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}.${paddedCentiseconds}`
}

export function getDuration(start: string, stop: string): string {
  const startSec = parseTimeToSeconds(start);
  const stopSec = parseTimeToSeconds(stop);
  const duration = stopSec - startSec;
  if (duration < 0) throw new Error('stop must be after start');
  return formatDuration(duration, false);
}

export function timeInputToUTCString(time: string): string {
  const [h, m, s = "00"] = time.split(":");

  const date = new Date();
  date.setUTCHours(parseInt(h), parseInt(m), parseInt(s), 0);

  return date.toISOString().split("T")[1]; // donne: "08:39:00.000Z"
}

export function formatDate(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) {
    return 'Date invalide';
  }

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  return date.toLocaleDateString('fr-FR', options);
}

export function formatHHhmm(time: string): string {
  if (!time) return "00h00"
  const [h, m] = time.split(":")
  return `${h.padStart(2, "0")}h${m.padStart(2, "0")}`
}

export function formatTime(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) {
    return '--:--';
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  return date.toLocaleTimeString('fr-FR', options);
}

export function formatDateTime(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) {
    return 'Date invalide';
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  const formattedDate = date.toLocaleDateString('fr-FR', dateOptions);
  const formattedTime = date.toLocaleTimeString('fr-FR', timeOptions);

  return `${formattedDate} à ${formattedTime}`;
}

export function getRelativeTime(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return formatDate(date);
  } else if (days > 0) {
    return `il y a ${days} jour${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `il y a ${hours} heure${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return 'à l\'instant';
  }
}

// --- Utilitaires Datalake ---

/**
 * Convertit un datetime-local vers le format ISO 8601 attendu par l'API
 * @param datetimeLocal - Format "YYYY-MM-DDTHH:mm" du input datetime-local
 * @returns Format "YYYY-MM-DDTHH:mm:ss"
 */
export function datetimeLocalToISO(datetimeLocal: string): string {
  if (!datetimeLocal) return ''
  // Ajouter les secondes si absentes
  if (datetimeLocal.length === 16) {  // "YYYY-MM-DDTHH:mm"
    return datetimeLocal + ":00"      // "YYYY-MM-DDTHH:mm:ss"
  }
  return datetimeLocal
}

/**
 * Formate une durée en secondes au format mm:ss ou hh:mm:ss
 * @param seconds - Durée en secondes
 * @returns Chaîne formatée
 */
export function formatDurationSeconds(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '0:00'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Formate un timestamp UTC en heure locale française
 * @param isoString - Timestamp ISO 8601
 * @returns Heure formatée (ex: "20:30:15")
 */
export function formatUTCTime(isoString: string): string {
  if (!isoString) return '--:--:--'

  try {
    const date = new Date(isoString)
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch {
    return '--:--:--'
  }
}

// --- Utilitaires pour l'affichage de l'heure réelle de diffusion ---

/**
 * Contraint une saisie au format HH:MM:SS pendant la frappe.
 *
 * Écarte tout caractère qui n'est ni un chiffre ni un deux-points, limite
 * chaque segment à 2 chiffres et n'insère JAMAIS de séparateur : c'est à
 * l'utilisateur de taper les deux-points, la saisie restant sinon bloquée dans
 * le segment courant. Comportement repris tel quel de l'édition des timecodes
 * de séquence, pour que toutes les saisies de temps de l'application se
 * ressemblent.
 */
export function validateTimecodeInput(value: string): string {
  if (!value) return ""

  const cleaned = value.replace(/[^\d:]/g, "")
  const parts = cleaned.split(":")
  const [h = "", m = "", s = ""] = parts.slice(0, 3)

  const hFormatted = h.slice(0, 2)
  const mFormatted = m.slice(0, 2)
  const sFormatted = s.slice(0, 2)

  if (parts.length === 1) return hFormatted
  if (parts.length === 2) return `${hFormatted}:${mFormatted}`
  return `${hFormatted}:${mFormatted}:${sFormatted}`
}

/** Vrai si la saisie forme un HH:MM:SS complet et exploitable */
export function isCompleteTimecode(value: string): boolean {
  return /^\d{1,2}:\d{1,2}:\d{1,2}$/.test(value.trim())
}

/**
 * Convertit des secondes en format HH:MM:SS
 * @param seconds - Nombre de secondes
 * @returns Format HH:MM:SS
 */
export function secondsToHHMMSS(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(ss)}`
}

/**
 * Formate le temps pour l'affichage (timecode classique)
 * @param seconds - Temps en secondes
 * @returns Format H:MM:SS ou MM:SS selon la durée
 */
export function formatTimeDisplay(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
}

/**
 * Formate le temps avec option d'heure réelle de diffusion
 * @param seconds - Temps relatif en secondes
 * @param emissionStartTime - Heure de début réelle en secondes depuis minuit (optionnel)
 * @returns Heure réelle si emissionStartTime fourni, sinon temps relatif
 */
export function formatTimeWithRealTime(seconds: number, emissionStartTime?: number): string {
  // Si emissionStartTime est fourni, afficher l'heure réelle de diffusion
  if (emissionStartTime !== undefined) {
    const totalSeconds = emissionStartTime + seconds
    const h = Math.floor(totalSeconds / 3600) % 24
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = Math.floor(totalSeconds % 60)
    const pad = (n: number) => String(n).padStart(2, "0")
    return `${pad(h)}:${pad(m)}:${pad(s)}`
  }
  // Sinon, afficher le temps relatif (comportement par défaut)
  return formatTimeDisplay(seconds)
}

/**
 * Formate l'heure réelle de diffusion (retourne null si non disponible)
 * @param seconds - Temps relatif en secondes
 * @param emissionStartTime - Heure de début réelle en secondes depuis minuit
 * @returns Heure réelle formatée ou null
 */
export function formatRealTime(seconds: number, emissionStartTime?: number): string | null {
  if (emissionStartTime === undefined) return null
  const totalSeconds = emissionStartTime + seconds
  const h = Math.floor(totalSeconds / 3600) % 24
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

/**
 * Convertit le temps relatif (HH:MM:SS) en heure réelle de diffusion
 * @param timeString - Temps relatif au format HH:MM:SS
 * @param emissionStartTime - Heure de début réelle en secondes depuis minuit
 * @returns Heure réelle au format HH:MM:SS ou temps relatif si emissionStartTime non disponible
 */
export function convertRelativeTimeToReal(timeString: string, emissionStartTime?: number): string {
  if (!timeString || emissionStartTime === undefined) return timeString || "00:00:00"
  const relativeSeconds = parseTimeToSeconds(timeString)
  return secondsToHHMMSS(emissionStartTime + relativeSeconds)
}

/**
 * Convertit l'heure réelle de diffusion en temps relatif
 * @param realTimeString - Heure réelle au format HH:MM:SS
 * @param emissionStartTime - Heure de début réelle en secondes depuis minuit
 * @returns Temps relatif au format HH:MM:SS ou heure réelle si emissionStartTime non disponible
 */
export function convertRealTimeToRelative(realTimeString: string, emissionStartTime?: number): string {
  if (!realTimeString || emissionStartTime === undefined) return realTimeString
  const realSeconds = parseTimeToSeconds(realTimeString)
  const relativeSeconds = realSeconds - emissionStartTime
  // S'assurer que le résultat n'est pas négatif
  return secondsToHHMMSS(Math.max(0, relativeSeconds))
}

/**
 * Supprime les accents et diacritiques d'une chaîne (é→e, à→a, ç→c, etc.)
 */
export function removeAccents(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

/**
 * Normalise une chaîne pour la recherche textuelle :
 * sans accents, tirets/underscores/apostrophes → espaces, minuscules.
 * Cohérent avec normalizeName (scoreUtils) et normalize_name (backend).
 */
export function normalizeForSearch(str: string): string {
  const withoutAccents = removeAccents(str)
  return withoutAccents
    .replace(/[-_]/g, " ")
    .replace(/['\u2018\u2019\u02BC]/g, " ")
    .toLowerCase()
}

/**
 * Normalise le nom d'une liste : majuscules, sans accents ni caractères spéciaux
 * @param name - Nom de la liste à normaliser
 * @returns Nom normalisé en majuscules sans accents ni caractères spéciaux
 */
export function normalizeListName(name: string): string {
  if (!name) return ""
  
  const withoutAccents = removeAccents(name)
  
  // Garder uniquement lettres, chiffres et espaces, remplacer le reste par des espaces
  const cleaned = withoutAccents.replace(/[^A-Za-z0-9\s]/g, " ")
  
  // Normaliser les espaces multiples en un seul espace
  const normalizedSpaces = cleaned.replace(/\s+/g, " ")
  
  // Mettre en majuscules et enlever les espaces en début/fin
  return normalizedSpaces.trim().toUpperCase()
}
